import imageCompression from 'browser-image-compression';
import { isDngImageFile, isHeicImageFile } from './imageValidation';

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  initialQuality?: number;
  fileType?: string;
  fallbackToOriginal?: boolean;
}

const withJpegExtension = (fileName: string): string => {
  const baseName = fileName.replace(/\.[^.]+$/, '');
  return `${baseName || 'image'}.jpg`;
};

/**
 * Compress an image file to optimize for upload
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Compressed image file
 */
export async function compressImage(
  file: File,
  options?: CompressionOptions
): Promise<File> {
  const defaultOptions: CompressionOptions = {
    maxSizeMB: 1, // Maximum file size in MB
    maxWidthOrHeight: 1920, // Maximum width or height
    useWebWorker: true,
    initialQuality: 0.8, // Initial encoder quality (0.1 to 1)
    fallbackToOriginal: true,
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
  };
  const { fallbackToOriginal, ...compressionOptions } = mergedOptions;

  const requiresHeicConversion = isHeicImageFile(file);
  const requiresDngConversion = isDngImageFile(file);

  try {
    let sourceFile = file;
    if (requiresHeicConversion) {
      const { heicTo } = await import('heic-to/csp');
      const convertedBlob = await heicTo({
        blob: file,
        type: 'image/jpeg',
        quality: 0.9,
      });
      sourceFile = new File(
        [convertedBlob],
        withJpegExtension(file.name),
        { type: 'image/jpeg', lastModified: file.lastModified },
      );
    }

    const outputOptions = requiresDngConversion
      ? { ...compressionOptions, fileType: 'image/jpeg' }
      : compressionOptions;
    const compressedFile = await imageCompression(sourceFile, outputOptions);
    const outputType = outputOptions.fileType || compressedFile.type || sourceFile.type;
    const outputName = outputType === 'image/jpeg'
      ? withJpegExtension(sourceFile.name)
      : sourceFile.name;
    
    return new File([compressedFile], outputName, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error('Error compressing image:', error);
    if (requiresHeicConversion || requiresDngConversion || !fallbackToOriginal) {
      throw error;
    }
    // Return original file if compression fails
    return file;
  }
}

/**
 * Product photos: 1600 px covers the largest real display (the gallery at about 750 px on a retina screen);
 * the server also makes an 800 px copy for cards and link previews.
 */
export const PRODUCT_IMAGE_OPTIONS: CompressionOptions = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  initialQuality: 0.8,
  fileType: 'image/jpeg',
  fallbackToOriginal: false,
};

/**
 * Get compression options for profile pictures
 */
export const PROFILE_IMAGE_OPTIONS: CompressionOptions = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 800,
  useWebWorker: true,
  initialQuality: 0.8,
  fallbackToOriginal: false,
};
